/**
 * WS-14 — IN-APP HELP content (static, structured). Short, honest how-tos for the console's headline
 * surfaces — no tour library, no chat widget, no remote fetch. Edited as TSX so a topic can carry
 * light emphasis/lists without a markdown pipeline. The HelpDrawer renders this list.
 */
import type { ReactNode } from 'react';

export interface HelpTopic {
  id: string;
  title: string;
  body: ReactNode;
}

/** Where the public roadmap/changelog lives (the marketing site). */
export const PUBLIC_ROADMAP_PATH = '/roadmap';
/** Support inbox — the same address the marketing site routes contact to. */
export const SUPPORT_EMAIL = 'hello@salonomy.com';

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'diary-queue',
    title: 'Diary & the walk-in queue',
    body: (
      <>
        <p>
          The <strong>Diary</strong> is your day at a glance — one column per chair. Drag an
          appointment to reschedule, or focus a block and use the arrow keys: ↑/↓ nudge the time,
          ←/→ move it between chairs, then <kbd>Enter</kbd> to confirm.
        </p>
        <p>
          The <strong>Queue</strong> rail shows walk-ins waiting. Call the next client to seat them
          straight onto the diary; the public queue link lets walk-ins add themselves.
        </p>
      </>
    ),
  },
  {
    id: 'till',
    title: 'Taking payment at the till',
    body: (
      <>
        <p>
          Open a completed appointment and choose <strong>Take payment</strong>. Add tips, split
          across card and cash, redeem a voucher, package or loyalty points, then take the money.
        </p>
        <p>
          Every payment writes a receipt you can re-send. The day's running total sits in the rail
          under <strong>Today, salon-wide</strong>.
        </p>
      </>
    ),
  },
  {
    id: 'payments',
    title: 'Payments & refunds',
    body: (
      <>
        <p>
          <strong>Payments</strong> (managers) is the back office: the takings ledger, receipt
          history, refunds, voids and no-show fees. Refunds and voids are manager-gated and always
          leave an audit trail — money is never silently rewritten.
        </p>
      </>
    ),
  },
  {
    id: 'stock',
    title: 'Stock & products',
    body: (
      <>
        <p>
          <strong>Stock</strong> (managers) holds your product catalogue, suppliers and on-hand
          levels. Record deliveries and adjustments; on-hand and valuation are derived from the
          movement ledger, so the numbers always reconcile.
        </p>
      </>
    ),
  },
  {
    id: 'comms',
    title: 'Comms & campaigns',
    body: (
      <>
        <p>
          <strong>Comms</strong> (managers) sends reminders and campaigns by SMS and email. Draft a
          campaign, run the <strong>preview</strong> to see the recipient count and estimated cost,
          then send — the preview is your spend safety net before anything goes out.
        </p>
      </>
    ),
  },
  {
    id: 'reports',
    title: 'Reports',
    body: (
      <>
        <p>
          <strong>Reports</strong> (managers) covers takings, future revenue, occupancy, staff
          performance and payment costs. Pick a date range; every figure is computed server-side, so
          what you read is what was banked.
        </p>
      </>
    ),
  },
];
